/**
 * bills.provisions_tracker — at-a-glance dashboard widget for the
 * NDAA-style provisions campaign. Shows the workspace's active
 * provisions with their derived status pills, grouped by what's
 * surviving vs dead.
 *
 * Optional params:
 *   - target_bill_ref: scope to a single vehicle (one NDAA cycle)
 *   - limit: max rows to show
 */
import { useQuery } from "@tanstack/react-query";
import { DataUnavailable } from "../../../framework/react/DataUnavailable.js";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { WidgetProps } from "../registry";
import { Skeleton } from "../../components/ui";

type Status = "proposed" | "in_amendment" | "in_conference" | "enacted" | "dead";

interface Provision {
  id: string;
  name: string;
  target_bill_ref?: string | null;
  source_bill_ref?: string | null;
  status: Status;
  derived_status: Status;
  amendment_count?: number;
}

const STATUS_STYLE: Record<Status, { label: string; bg: string; fg: string; rank: number }> = {
  enacted:       { label: "Enacted",        bg: "bg-success-soft", fg: "text-success",  rank: 0 },
  in_conference: { label: "In conference",  bg: "bg-info-soft",  fg: "text-info",    rank: 1 },
  in_amendment:  { label: "In amendment",   bg: "bg-warn-soft",  fg: "text-warn",  rank: 2 },
  proposed:      { label: "Proposed",       bg: "bg-stone-100",  fg: "text-stone-700",  rank: 3 },
  dead:          { label: "Dead",           bg: "bg-danger-soft", fg: "text-danger",    rank: 4 },
};

function formatBillRef(ref?: string | null): string {
  if (!ref) return "";
  const m = ref.match(/^(\d+)-(hr|s|hres|sres|hjres|sjres|hconres|sconres)-(\d+)$/);
  if (!m) return ref;
  const map: Record<string, string> = {
    hr: "H.R.", s: "S.", hres: "H.Res.", sres: "S.Res.",
    hjres: "H.J.Res.", sjres: "S.J.Res.", hconres: "H.Con.Res.", sconres: "S.Con.Res.",
  };
  return `${map[m[2]] || m[2]} ${m[3]}`;
}

export function ProvisionsTracker({ spec, workspaceId }: WidgetProps) {
  const targetBillRef = spec.params?.target_bill_ref as string | undefined;
  const limit = (spec.params?.limit as number) ?? 10;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["widget", "provisions_tracker", workspaceId, targetBillRef ?? "all"],
    queryFn: async () => {
      const url = targetBillRef
        ? `/api/v1/workspaces/${workspaceId}/provisions?target_bill_ref=${encodeURIComponent(targetBillRef)}`
        : `/api/v1/workspaces/${workspaceId}/provisions`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load provisions");
      return res.json();
    },
  });

  if (error) return <DataUnavailable message="Tracked provisions could not be loaded." retry={refetch} />;
  if (isLoading) return <div role="status" aria-live="polite" className="space-y-2"><span className="sr-only">Loading…</span><Skeleton className="h-3 w-full" /><Skeleton className="h-3 w-4/5" /><Skeleton className="h-3 w-3/5" /></div>;
  const provisions: Provision[] = data?.provisions ?? [];

  if (provisions.length === 0) {
    return (
      <div className="text-xs text-stone-600">
        No provisions tracked yet. <Link to="/provisions" className="inline-flex items-center gap-0.5 font-medium text-action hover:underline">Start a campaign<ArrowRight size={11} aria-hidden="true" /></Link>
      </div>
    );
  }

  // Rollup
  const rollup = provisions.reduce(
    (acc, p) => {
      acc[p.derived_status] = (acc[p.derived_status] ?? 0) + 1;
      return acc;
    },
    { proposed: 0, in_amendment: 0, in_conference: 0, enacted: 0, dead: 0 } as Record<Status, number>
  );

  const sorted = [...provisions].sort(
    (a, b) => STATUS_STYLE[a.derived_status].rank - STATUS_STYLE[b.derived_status].rank
  );

  return (
    <div className="text-xs space-y-2">
      <div className="flex items-baseline justify-between border-b border-stone-100 pb-1.5">
        <div className="text-stone-700 font-semibold">{provisions.length} provisions</div>
        <Link to="/provisions" className="inline-flex items-center gap-0.5 text-[11px] font-medium text-action hover:underline">View all<ArrowRight size={11} aria-hidden="true" /></Link>
      </div>
      <div className="flex flex-wrap gap-1">
        {(["enacted", "in_conference", "in_amendment", "proposed", "dead"] as Status[]).map((s) =>
          rollup[s] > 0 ? (
            <span
              key={s}
              className={`px-1.5 py-0.5 rounded ${STATUS_STYLE[s].bg} ${STATUS_STYLE[s].fg} text-[10px] font-bold uppercase tracking-wide`}
            >
              {rollup[s]} {STATUS_STYLE[s].label}
            </span>
          ) : null
        )}
      </div>
      <ul className="space-y-1.5">
        {sorted.slice(0, limit).map((p) => {
          const ss = STATUS_STYLE[p.derived_status];
          return (
            <li key={p.id} className="flex items-center gap-2">
              <span className={`tier-tag text-[9px] ${ss.bg} ${ss.fg} shrink-0 w-20 text-center`}>
                {ss.label}
              </span>
              <Link to="/provisions" className="flex-1 truncate text-stone-700 hover:text-action hover:underline">
                {p.name}
              </Link>
              {p.amendment_count != null && p.amendment_count > 0 && (
                <span className="font-mono text-stone-400 text-[10px] shrink-0">
                  {p.amendment_count}am
                </span>
              )}
              {p.source_bill_ref && (
                <span className="font-mono text-stone-400 text-[10px] shrink-0" title={p.source_bill_ref}>
                  {formatBillRef(p.source_bill_ref)}
                </span>
              )}
            </li>
          );
        })}
      </ul>
      {sorted.length > limit && (
        <div className="text-[10px] text-stone-400 italic pt-1">
          +{sorted.length - limit} more
        </div>
      )}
    </div>
  );
}
